import { MouseEvent, useCallback } from 'react';
import { Product } from '@interfaces';
import {
  RootDiv,
  TitleTypography,
  DetailsTextTypography,
  SkeletonImg,
  ImgProduct,
  InfoDiv,
  PriceContainer,
  PriceTypography,
} from './LastWhimProductCard.styles';
import { extractExtraInfo } from '@screens/product/utils/extractExtraInfo';
import { useUpdateQuantity } from '@screens/product/hooks/useUpdateQuantity';
import { useEffect, useState } from 'react';
import { NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { useRouter } from 'next/router';
import { useSegment } from '@hooks/useSegment';
import { ProductQuantityActions } from '@components/Product/ProductQuantityActions';
import { useAddProduct } from '@hooks/useAddProduct';
import { Skeleton } from '@mui/material';

const currencyFormatter = new CurrencyFormatter({
  fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
});

const defaultImage = '/assets/images/default.webp';

interface LastWhimProductCardProps {
  product: Product;
  loading?: boolean;
}

export const LastWhimProductCard = ({
  product,
  loading,
}: LastWhimProductCardProps) => {
  const { track } = useSegment();
  const router = useRouter();

  const {
    handleAddProduct: handleAddProductToCart,
    loading: addProductToCartLoading,
  } = useAddProduct({
    location: 'lastWhim',
  });

  const { addQuantity, subQuantity, quantity, MAX_AMOUNT } = useUpdateQuantity({
    product,
    price: product.price,
    location: 'lastWhim',
  });

  const [imageUrl, setImageUrl] = useState(product?.imagesUrl?.[1]);

  const hasDiscount = product?.discount?.price > 0;
  const price = hasDiscount
    ? currencyFormatter.format(product?.discount?.listPrice ?? 0)
    : currencyFormatter.format(product?.price ?? 0);
  const discountPrice = currencyFormatter.format(product?.discount?.price ?? 0);

  const handleAddProduct = useCallback(async () => {
    if (quantity === 0) {
      await handleAddProductToCart({ puuid: product?.uuid, product });
    }
    addQuantity();
  }, [handleAddProductToCart, product, addQuantity, quantity]);

  const handleImageError = useCallback(() => {
    setImageUrl(defaultImage);
  }, [defaultImage]);

  const handleClick = useCallback(
    (elem: Product): void => {
      void track('product select', {
        id: product?.id,
        name: product?.name,
        brand: product?.makerBrand ?? product?.laboratory,
        prescriptionType: product?.prescriptionType ?? 'null',
        category: product?.categories?.map((c) => c.name).join(', ') ?? 'null',
        search: 'null',
        availability: product?.availability?.status,
        location: 'lastWhim',
      });
      void router.push({
        pathname: `/producto/${product.slug}`,
      });
    },
    [product, router, track],
  );

  useEffect(
    function syncronizeImage() {
      setImageUrl(product?.imagesUrl?.[1]);
    },
    [product?.imagesUrl],
  );

  if (loading) return <LastWhimProductCardSkeleton />;

  return (
    <RootDiv>
      <ImgProduct
        src={imageUrl}
        onError={handleImageError}
        placeholder="blur"
        onClick={() => handleClick(product)}
      />
      <InfoDiv onClick={() => handleClick(product)}>
        <TitleTypography>{product.name}</TitleTypography>
        <DetailsTextTypography>
          {extractExtraInfo(product)}
        </DetailsTextTypography>
        <PriceContainer>
          <PriceTypography isOldPrice={hasDiscount}>{price}</PriceTypography>
          {hasDiscount && <PriceTypography>{discountPrice}</PriceTypography>}
        </PriceContainer>
      </InfoDiv>
      <ProductQuantityActions
        quantity={quantity}
        addQuantity={handleAddProduct}
        subQuantity={quantity > 0 ? subQuantity : undefined}
        MAX_AMOUNT={MAX_AMOUNT}
        loading={addProductToCartLoading}
      />
    </RootDiv>
  );
};

export const LastWhimProductCardSkeleton = () => {
  return (
    <RootDiv>
      <SkeletonImg variant="rectangular" />
      <InfoDiv>
        <TitleTypography>
          <Skeleton variant="text" width={300} height={21} />
          <Skeleton variant="text" width={200} height={18} />
          <Skeleton variant="text" width={100} height={21} />
        </TitleTypography>
      </InfoDiv>
    </RootDiv>
  );
};
